export const FIELD_TYPE_OPTIONS = [
  'Text',
  // 'Select',
  'Dropdown',
  'Radio',
  'Number',
  'Email',
  'Date',
  // 'MultiSelect',
  'Text Area',
  'Checkbox',
  // 'Mobile',
  'File',
  'Images',
  'Time',
];

export const OPTION_FIELD_TYPES = new Set([
  'Select',
  'Dropdown',
  'Radio',
  'Checkbox',
  // 'MultiSelect',
]);

export const FIELD_ACCESS_OPTIONS = ['Both', 'User', 'Admin'];

export const STATUS_COLORS = {
  Open: '#FF0000',
  'Under Verification': '#FF6F00',
  Resolved: '#00D82F',
  Closed: '#006FFF',
};

export const STATUS_OPTION_ORDER = ['Open', 'Under Verification', 'Resolved', 'Closed'];

export const STATUS_PRESET = {
  id: 'default-status',
  fieldKey: 'status',
  fieldLabel: 'Status',
  originalFieldName: 'Status',
  fieldType: 'Custom',
  required: true,
  fieldAccess: '',
  hideFor: [],
  dependsOn: '',
  options: [...STATUS_OPTION_ORDER],
  optionColors: { ...STATUS_COLORS },
  parentMappings: {},
  lockMode: 'options',
  isNew: false,
};

export const PRIORITIES_PRESET = {
  id: 'custom-priorities',
  fieldKey: 'priorities',
  fieldLabel: 'Priorities',
  originalFieldName: 'Priorities',
  fieldType: 'Dropdown',
  required: false,
  fieldAccess: '',
  hideFor: [],
  dependsOn: '',
  options: ['Urgent', 'High', 'Medium', 'Low'],
  parentMappings: {},
  lockMode: 'options',
  isNew: false,
};

export const STORE_ID_PRESET = {
  id: 'custom-store-id',
  fieldKey: 'storeIdCustom',
  fieldLabel: 'Store ID',
  originalFieldName: 'Store ID',
  fieldType: 'Custom',
  required: false,
  fieldAccess: '',
  hideFor: [],
  dependsOn: '',
  options: [],
  optionColors: {},
  parentMappings: {},
  lockMode: 'all',
  isNew: false,
};

export const CUSTOM_FIELD_LIBRARY = [
  {
    ...STATUS_PRESET,
    label: STATUS_PRESET.fieldLabel,
  },
  {
    ...STORE_ID_PRESET,
    label: STORE_ID_PRESET.fieldLabel,
  },
];

function normalizeFieldName(value) {
  return String(value || '').trim().toLowerCase();
}

export function isStatusField(field) {
  const key = normalizeFieldName(field?.fieldKey);
  const label = normalizeFieldName(field?.fieldLabel || field?.label);
  return key === 'status' || key === 'statuses' || key === 'statuscustom'
    || label === 'status' || label === 'statuses';
}

export function isLockedStatusField(field) {
  return isStatusField(field) && (field?.lockMode === 'options' || field?.lockMode === 'all');
}

function isPrimaryStatusField(field, fields = []) {
  if (!isStatusField(field)) return false;
  const list = fields.length ? fields : [field];
  const persistedStatus = list.filter((item) => !item?.isNew && isStatusField(item));
  const lockedStatus = list.filter(isLockedStatusField);
  const primary = persistedStatus[0] || lockedStatus[0] || list.find((item) => isStatusField(item) && !item?.isNew);
  if (!primary) return !field?.isNew;
  return String(primary.id) === String(field.id);
}

export function isPrioritiesField(field) {
  const key = String(field?.fieldKey || '').toLowerCase();
  const label = String(field?.fieldLabel || field?.label || '').trim().toLowerCase();
  return key === 'priorities' || label === 'priorities';
}

export function isStoreIdField(field) {
  const key = String(field?.fieldKey || '').toLowerCase();
  const label = String(field?.fieldLabel || field?.label || '').trim().toLowerCase();
  return key === 'storeid' || key === 'storeidcustom' || label === 'store id';
}

export function isLockedStatusOption(option) {
  const key = String(option || '').trim().toLowerCase();
  return key === 'open' || key === 'closed';
}

export function isDefaultStatusOption(option) {
  return String(option || '').trim().toLowerCase() === 'open';
}

export function sortStatusOptions(options = []) {
  const list = (options || []).filter((item) => item != null && String(item).trim() !== '');
  const used = new Set();
  const ordered = [];
  STATUS_OPTION_ORDER.forEach((canonical) => {
    const match = list.find((item) => String(item).trim().toLowerCase() === canonical.toLowerCase());
    if (match == null || used.has(match)) return;
    ordered.push(match);
    used.add(match);
  });
  list.forEach((item) => {
    if (!used.has(item)) {
      ordered.push(item);
      used.add(item);
    }
  });
  return ordered;
}

// Duplicate of isStatusField; callers use isStatusField directly.
// export function isColorOptionsField(field) {
//   return isStatusField(field);
// }

export function isOptionFieldType(field) {
  return OPTION_FIELD_TYPES.has(field?.fieldType) || isLockedStatusField(field);
}

export function optionFieldMissingOptions(field) {
  if (!field || isStatusField(field) || isStoreIdField(field)) return false;
  if (!OPTION_FIELD_TYPES.has(field.fieldType)) return false;
  return !(field.options || []).some((option) => String(option || '').trim());
}

export function formHasOptionFieldMissingOptions(fields = []) {
  return fields.some(optionFieldMissingOptions);
}

export function missingOptionsMessage(fields = []) {
  const types = [];
  const seen = new Set();
  fields.forEach((field) => {
    if (!optionFieldMissingOptions(field)) return;
    const type = String(field.fieldType || '').trim();
    if (!type || seen.has(type)) return;
    seen.add(type);
    types.push(type);
  });
  if (!types.length) return '';
  if (types.length === 1) return `Add options for ${types[0]} field.`;
  if (types.length === 2) return `Add options for ${types[0]} and ${types[1]} fields.`;
  return `Add options for ${types.slice(0, -1).join(', ')}, and ${types[types.length - 1]} fields.`;
}

function hasSameLabelAndType(left, right) {
  const leftLabel = String(left?.fieldLabel || left?.label || '').trim().toLowerCase();
  const rightLabel = String(right?.fieldLabel || right?.label || '').trim().toLowerCase();
  const leftType = String(left?.fieldType || '').trim().toLowerCase();
  const rightType = String(right?.fieldType || '').trim().toLowerCase();
  return Boolean(leftLabel && rightLabel && leftType && rightType && leftLabel === rightLabel && leftType === rightType);
}

export function fieldHasDuplicateLabelAndType(candidate, fields = []) {
  if (!candidate) return false;
  return fields.some((field) => {
    if (String(field.id) === String(candidate.id)) return false;
    return hasSameLabelAndType(candidate, field);
  });
}

export function formHasDuplicateLabelAndType(fields = []) {
  return fields.some((field) => fieldHasDuplicateLabelAndType(field, fields));
}

export function toUiFieldType(apiFieldType, typeMap = {}, fieldKey = '') {
  const key = String(fieldKey || '');
  if (isStatusField({ fieldKey: key }) || isStoreIdField({ fieldKey: key })) {
    return typeMap.CUSTOM ?? 'Custom';
  }
  if (apiFieldType === 'MULTI_SELECT' || apiFieldType === 'MULTISELECT') return typeMap.CHECKBOX ?? 'Checkbox';
  if (apiFieldType === 'PHONE' || apiFieldType === 'MOBILE') return typeMap.NUMBER ?? 'Number';
  if (apiFieldType === 'FILE' && key.endsWith('Images')) return typeMap.IMAGES ?? 'Images';
  if (apiFieldType === 'DATETIME' && key.endsWith('Time') && !key.endsWith('DateTime')) {
    return typeMap.TIME ?? 'Time';
  }
  if (apiFieldType === 'SELECT' || apiFieldType === 'DROPDOWN') {
    const lowerKey = key.toLowerCase();
    if (lowerKey.endsWith('dropdown')) return typeMap.DROPDOWN ?? 'Dropdown';
    if (lowerKey.endsWith('select')) return typeMap.SELECT ?? 'Select';
  }
  return typeMap[apiFieldType] ?? apiFieldType ?? 'Custom';
}

// Unused: FormCard uses a fixed "Enter option" placeholder.
// export function optionEntryPlaceholder(fieldType) {
//   return 'Enter option';
// }

export function optionsColumnLabel(field) {
  return isPrioritiesField(field) ? 'Priorities' : 'Option';
}

export function optionsPanelDescription(field, showParentMapping = false) {
  if (!field) return '';
  if (showParentMapping) return 'Define options and set parent.';
  if (isStatusField(field)) return 'Manage the status';
  return 'Manage your fields.';
}

export function applyPresetMeta(field, fields = []) {
  if (String(field?.fieldType || '') === 'MultiSelect') {
    return applyPresetMeta({ ...field, fieldType: 'Checkbox' }, fields);
  }
  if (isStatusField(field)) {
    if (field?.isNew || !isPrimaryStatusField(field, fields)) {
      return {
        ...field,
        lockMode: field?.lockMode === 'all' ? field.lockMode : undefined,
      };
    }
    const incomingLabel = String(field.fieldLabel || field.label || '').trim();
    const useDefaultLabel = !incomingLabel || incomingLabel.toLowerCase() === 'statuses';
    return {
      ...STATUS_PRESET,
      ...field,
      fieldKey: STATUS_PRESET.fieldKey,
      fieldLabel: useDefaultLabel ? STATUS_PRESET.fieldLabel : incomingLabel,
      originalFieldName: useDefaultLabel
        ? STATUS_PRESET.originalFieldName
        : (field.originalFieldName ?? incomingLabel),
      fieldType: STATUS_PRESET.fieldType,
      required: true,
      options: sortStatusOptions(field.options?.length ? field.options : STATUS_PRESET.options),
      optionColors: { ...STATUS_PRESET.optionColors, ...field.optionColors },
      lockMode: 'options',
    };
  }
  if (isPrioritiesField(field)) {
    return {
      ...PRIORITIES_PRESET,
      ...field,
      fieldKey: field.fieldKey || PRIORITIES_PRESET.fieldKey,
      fieldType: PRIORITIES_PRESET.fieldType,
      options: field.options?.length ? field.options : PRIORITIES_PRESET.options,
      lockMode: 'options',
    };
  }
  if (isStoreIdField(field)) {
    return {
      ...STORE_ID_PRESET,
      ...field,
      fieldKey: STORE_ID_PRESET.fieldKey,
      fieldType: STORE_ID_PRESET.fieldType,
      lockMode: 'all',
    };
  }
  return field;
}

export function withDefaultStatus(fields = []) {
  const mapped = fields.map((field) => applyPresetMeta(field, fields));
  if (mapped.some(isStatusField)) return mapped;
  const defaultStatus = { ...STATUS_PRESET };
  return [applyPresetMeta(defaultStatus, [defaultStatus, ...mapped]), ...mapped];
}
